import { observer } from "mobx-react-lite"
import * as yup from "yup"
import styles from "../sharedStyles.module.scss"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { getAuth } from "firebase/auth"
import { CruisePassengerAgeGroup } from "@cruisen/contract"
import { FormPhoneInput } from "../../../../atoms/FormPhoneInput"
import { isPhoneValid } from "../../../../../utils/validation"
import classNames from "classnames"
import FormTextInput from "../../../../atoms/FormTextInput"
import { colors } from "@theme/colors"
import { ModalCheckbox } from "../../../../atoms/ModalCheckbox"

type PersonalDetailsForm = {
  firstName: string
  lastName: string
  phoneNumber: string
}

const personalDetailsSchema = yup.object().shape({
  firstName: yup.string().required("-"),
  lastName: yup.string().required("-"),
  phoneNumber: yup.string().required("-"),
})

export const PersonalDetails = observer(
  ({
    onSubmit,
    formValues,
    termsAccepted,
    setTermsAccepted,
    isUserRegistrationPending,
  }: {
    onSubmit: (data: PersonalDetailsForm) => void
    formValues: PersonalDetailsForm
    termsAccepted: boolean
    setTermsAccepted: () => void
    isUserRegistrationPending: boolean
  }) => {
    const {
      control,
      formState: { isValid },
      handleSubmit,
      watch,
    } = useForm({
      defaultValues: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        phoneNumber: formValues.phoneNumber,
      },
      resolver: yupResolver(personalDetailsSchema),
    })

    const phoneNumber = watch("phoneNumber")
    const isPhoneNumberValid = isPhoneValid(phoneNumber)
    const currentUser = getAuth().currentUser

    const handleSubmitPersonalDetails = (data: PersonalDetailsForm) => {
      const agentCode = currentUser?.uid

      const newUserPayload = {
        id: currentUser?.uid as string,
        email: currentUser?.email as string,
        firstName: data.firstName,
        lastName: data.lastName,
        ageGroup: "adult" as CruisePassengerAgeGroup,
        phoneNumber: data.phoneNumber,
        agentCode,
      }

      onSubmit(newUserPayload)
    }

    return (
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(handleSubmitPersonalDetails)()
        }}
      >
        <div className={styles.inputsContainer}>
          <FormTextInput
            control={control}
            testID="first-name-input"
            autoFocus
            name="firstName"
            label="First name*"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <FormTextInput
            control={control}
            testID="last-name-input"
            name="lastName"
            label="Last name*"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <FormPhoneInput
            control={control}
            testID="phone-number-input"
            name="phoneNumber"
            label="Phone number*"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <div className={styles.termsAcceptedContainer}>
            <ModalCheckbox
              onPress={setTermsAccepted}
              isChecked={termsAccepted}
              testID="terms-and-conditions-checkbox"
            />
            <span>
              By signing up, you agree to Cruisen's{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://app.termly.io/policy-viewer/policy.html?policyUUID=b30f45eb-cf06-4d1b-a3ae-b4d0c098333a"
              >
                Terms and Conditions
              </a>
              ,{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://app.termly.io/policy-viewer/policy.html?policyUUID=ff1ed9d3-6931-4bc9-8258-f6f4b374e3b1"
              >
                Acceptable Use Policy
              </a>
              , and{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://app.termly.io/policy-viewer/policy.html?policyUUID=6072e786-6ce3-4b9e-8019-5f4b405dbc49"
              >
                Privacy Policy
              </a>
              .
            </span>
          </div>
        </div>
        <button
          type="submit"
          className={classNames(styles.submitButton, styles.button)}
          disabled={!isValid || !isPhoneNumberValid || !termsAccepted || isUserRegistrationPending}
          data-testid="go-to-profile-info-button"
        >
          {isUserRegistrationPending ? "Loading..." : "Next"}
        </button>
      </form>
    )
  },
)
